'use client';
import Markdown from '@repo/ui/src/components/Markdown';
import useCurrentUrl from '@repo/ui/src/hooks/useCurrentUrl';
import { classNames } from '@repo/ui/src/utils/classNames';
import React from 'react';

export interface ProductHeadingProduct {
  title: string;
  sku: string;
  smallHeading?: string;
  shortTitleMain?: string;
  shortTitlePrimary?: string;
  shortDescription?: string;
  ctaButtonType?: string;
}

export interface ProductHeadingProps {
  product: ProductHeadingProduct;
  className?: string;
  dark?: boolean;
  hideDescription?: boolean;
  hideSKU?: boolean;
  titleVariant?: string;
  banner?: React.ReactNode;
  rating?: React.ReactNode;
  price?: React.ReactNode;
}

const ProductHeading = ({
  product,
  className,
  dark,
  hideDescription,
  hideSKU = false,
  titleVariant = 'tra-text-web-heading-2',
  banner,
  rating,
  price,
}: ProductHeadingProps) => {
  const { eCommEnabled } = useCurrentUrl();

  return (
    <div
      className={classNames(
        'px-6 md:pr-[5vw] md:pl-0',
        dark && 'md:bg-transparent text-white',
        !hideDescription && 'pb-8',
        className,
      )}
    >
      {banner && <div className="flex justify-center md:justify-start mb-2 md:mb-5">{banner}</div>}
      <h1 className={classNames('text-center md:text-left', titleVariant)}>
        {product.smallHeading && (
          <span className="tra-text-web-sub-2 uppercase block">{product.smallHeading}</span>
        )}
        {product.shortTitleMain ? (
          <>
            {product.shortTitleMain} <span>{product.shortTitlePrimary}</span>
          </>
        ) : (
          product.title
        )}
      </h1>
      <div
        className={classNames(
          'flex gap-4 py-2 md:pt-4 md:pb-0 items-center justify-center md:justify-start',
          hideDescription && 'pb-0',
        )}
      >
        {rating && <a href="#reviews">{rating}</a>}
        {!hideSKU && eCommEnabled && (
          <span
            className={classNames(
              'self-stretch border-l',
              dark ? 'border-warm-400' : 'border-neutral-300',
            )}
          />
        )}
        {!hideSKU && eCommEnabled && (
          <p itemProp="sku" className={classNames(dark ? 'text-warm-400' : 'text-neutral-300')}>
            {product.sku}
          </p>
        )}
      </div>
      {!hideDescription && (
        <div className="pt-4">
          <Markdown bodySize="xLarge" className={classNames(dark && 'text-white')}>
            {product.shortDescription}
          </Markdown>
        </div>
      )}
      {price && product.ctaButtonType !== 'noCTA' && <div className="mt-4 flex">{price}</div>}
    </div>
  );
};

export default ProductHeading;
